import {
  FileText,
  Tag,
  CheckCircle,
  ListChecks,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { Button } from "../../components/ui";

const SENTIMENT_CONFIG = {
  positive: { label: "Positive", bg: "#f0fff4", color: "#276749" },
  neutral: { label: "Neutral", bg: "#f7fafc", color: "#4a5568" },
  mixed: { label: "Mixed", bg: "#fffbeb", color: "#92400e" },
  negative: { label: "Negative", bg: "#fff5f5", color: "#c53030" },
};

export default function MeetingSummary({
  summary,
  status,
  onRegenerate,
  regenerating,
}) {
  if (!summary) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: 32,
          color: colors.textSecondary,
          fontSize: 13,
        }}
      >
        {status === "processing" ? (
          <>
            <Loader2
              size={20}
              className="spin"
              style={{
                marginBottom: 8,
              }}
            />
            <div>Summary will be generated after transcription completes.</div>
          </>
        ) : status === "uploaded" || status === "pending-download" ? (
          'Click "Process Recording" to generate a transcript and summary.'
        ) : regenerating ? (
          <>
            <Loader2
              size={20}
              className="spin"
              style={{
                marginBottom: 8,
              }}
            />
            <div>Summary is being generated...</div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>No summary available.</div>
            {onRegenerate && (
              <Button
                variant="secondary"
                size="sm"
                icon={RefreshCw}
                onClick={onRegenerate}
                disabled={regenerating}
              >
                Generate Summary
              </Button>
            )}
          </>
        )}
      </div>
    );
  }

  const sentiment = SENTIMENT_CONFIG[summary.sentiment] || SENTIMENT_CONFIG.neutral;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Sentiment + metadata */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            fontSize: 11,
            padding: "3px 10px",
            borderRadius: 12,
            background: sentiment.bg,
            color: sentiment.color,
            fontWeight: 600,
          }}
        >
          {sentiment.label}
        </span>
        {summary.generatedAt && (
          <span style={{ fontSize: 11, color: colors.textSecondary }}>
            Generated{" "}
            {new Date(summary.generatedAt).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
            })}
            {summary.model ? ` via ${summary.model}` : ""}
          </span>
        )}
        {onRegenerate && (
          <RefreshCw
            size={13}
            color={colors.textSecondary}
            style={{ cursor: "pointer", marginLeft: "auto" }}
            onClick={onRegenerate}
            title="Regenerate summary"
          />
        )}
      </div>

      {/* Executive Summary */}
      <Section icon={FileText} title="Executive Summary">
        <p
          style={{
            fontSize: 13,
            lineHeight: 1.7,
            color: colors.text,
            whiteSpace: "pre-wrap",
            margin: 0,
          }}
        >
          {summary.executive}
        </p>
      </Section>

      {/* Key Topics */}
      {summary.keyTopics?.length > 0 && (
        <Section icon={Tag} title="Key Topics">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {summary.keyTopics.map((t, i) => (
              <div key={i}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: colors.text,
                  }}
                >
                  {t.topic}
                </div>
                {t.detail && (
                  <div
                    style={{
                      fontSize: 12,
                      color: colors.textSecondary,
                      marginTop: 2,
                    }}
                  >
                    {t.detail}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Decisions */}
      {summary.decisions?.length > 0 && (
        <Section icon={CheckCircle} title="Decisions">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {summary.decisions.map((d, i) => (
              <div key={i}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: colors.text,
                  }}
                >
                  {d.decision}
                </div>
                {d.context && (
                  <div
                    style={{
                      fontSize: 12,
                      color: colors.textSecondary,
                      marginTop: 2,
                    }}
                  >
                    {d.context}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Action Items */}
      {summary.actionItems?.length > 0 && (
        <Section icon={ListChecks} title="Action Items">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {summary.actionItems.map((a, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  padding: "6px 0",
                  borderBottom: `1px solid ${colors.borderLight}`,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: colors.text }}>
                    {a.action}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: colors.textSecondary,
                      marginTop: 2,
                      display: "flex",
                      gap: 12,
                    }}
                  >
                    {a.assignee && a.assignee !== "Unassigned" && (
                      <span>Assignee: {a.assignee}</span>
                    )}
                    {a.deadline && a.deadline !== "Not specified" && (
                      <span>Due: {a.deadline}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 8,
        }}
      >
        <Icon size={14} color={colors.textSecondary} />
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: colors.textSecondary,
          }}
        >
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}
